import React, { useEffect, useState } from 'react';
import type { FC, ChangeEvent, FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';

const API_BASE_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

interface SmtpState {
  port: string;
  email: string;
  password: string;
}

const SmtpSection: FC = () => {
  const [credentials, setCredentials] = useState<SmtpState>({
    port: '',
    email: '',
    password: '',
  });
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/smtp`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.success && data.settings) {
          setCredentials({
            port: data.settings.port || '',
            email: data.settings.email || '',
            password: data.settings.password || '',
          });
        }
      } catch (err) {
        console.error('Fetch SMTP settings error:', err);
        setError('Failed to load SMTP settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSuccess('');
    setError('');

    if (!credentials.port || !credentials.email || !credentials.password) {
      setError('Please fill out all fields.');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/smtp`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setSuccess('SMTP settings saved successfully!');
    } catch (err) {
      console.error('Save SMTP settings error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const fields: { key: keyof SmtpState; label: string; type: string; placeholder: string }[] = [
    { key: 'port', label: 'SMTP Port', type: 'number', placeholder: 'e.g., 587' },
    { key: 'email', label: 'Email', type: 'email', placeholder: 'your-email@example.com' },
    { key: 'password', label: 'Password', type: 'password', placeholder: '••••••••••••' },
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 sm:mb-8">SMTP Settings</h1>

      <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-sm space-y-4 sm:space-y-6">
        {loading && (
          <div className="flex items-center text-gray-600">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-2"></div>
            Loading current settings...
          </div>
        )}
        {success && <div className="p-3 text-green-800 bg-green-100 rounded-lg">{success}</div>}
        {error && <div className="p-3 text-red-800 bg-red-100 rounded-lg">{error}</div>}

        {fields.map(field => (
          <div key={field.key}>
            <label htmlFor={field.key} className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
            </label>
            <div className="relative">
              <input
                id={field.key}
                name={field.key}
                type={field.type === 'password' && showPassword ? 'text' : field.type}
                value={credentials[field.key]}
                onChange={handleInputChange}
                placeholder={field.placeholder}
                className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition ${field.type === 'password' ? 'pr-10' : ''}`}
                required
                disabled={loading || saving}
              />
              {field.type === 'password' && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-primary/90"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              )}
            </div>
          </div>
        ))}

        <div>
          <button
            type="submit"
            disabled={saving || loading}
            className="w-full sm:w-auto bg-primary text-white px-8 py-3 rounded-xl hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save SMTP Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SmtpSection;